// Motion when presenting (S7): how a slide comes in (a transition: none, fade, push or zoom, each quick and calm; the
// deck's, or the slide's own) and what on it appears on a click (builds: boxes and the layout's fields, in an order
// the user sets, each fading or rising in). Present and the shared viewer step through a slide's builds before the
// next slide; the editor, thumbnails, the PDF and the PowerPoint file show the slide as it ends, everything in place.
// Plain data on the slide and deck entries, cleaned against the slide's own boxes, so a box removed leaves no build.

import { FIELDS } from "./slideDesign";

// SC: Morph, as PowerPoint's: boxes on both slides glide and resize from one place to the other, the rest fade
export const TRANSITIONS = ["none", "fade", "push", "zoom", "morph"] as const;
export type Transition = (typeof TRANSITIONS)[number];
export const TRANSITION_NAMES: Record<Transition, string> = { none: "None", fade: "Fade", push: "Push", zoom: "Zoom", morph: "Morph" };

export const EFFECTS = ["fade", "rise"] as const;
export type BuildEffect = (typeof EFFECTS)[number];
export const EFFECT_NAMES: Record<BuildEffect, string> = { fade: "Fade in", rise: "Rise in" };

/** One thing that appears on a click: a box by its id, or a layout's field as "f:title". */
export interface Build {
  id: string;
  effect: BuildEffect;
}

/** The most builds a slide has. */
export const MAX_BUILDS = 40;

/** Fixes 8: the longest a slide stays before the talk moves on by itself (PowerPoint's After), in seconds. */
export const MAX_ADVANCE = 3600;

/** How long a slide stays before the talk moves on by itself, in whole seconds; null for on a click only. */
export function cleanAdvance(raw: unknown): number | null {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return null;
  return Math.min(MAX_ADVANCE, Math.max(1, Math.round(raw)));
}

/** How long each takes, in milliseconds: quick enough not to wait for, slow enough to see. */
export const TRANSITION_MS: Record<Transition, number> = { none: 0, fade: 320, push: 420, zoom: 360, morph: 720 };

export const isTransition = (x: unknown): x is Transition => typeof x === "string" && (TRANSITIONS as readonly string[]).includes(x);

/** A transition as written: one of ours, or a word for one (dissolve is a fade, slide and wipe are a push). */
export function transitionFrom(raw: unknown): Transition | null {
  if (typeof raw !== "string") return null;
  const k = raw.trim().toLowerCase();
  if (isTransition(k)) return k;
  const alias: Record<string, Transition> = { off: "none", cut: "none", dissolve: "fade", crossfade: "fade", "cross-fade": "fade", fadein: "fade", slide: "push", wipe: "push", cover: "push", reveal: "push", scale: "zoom", grow: "zoom" };
  return alias[k] ?? null;
}

const FIELD_KEYS = new Set(FIELDS.map((f) => `f:${f}`));

/** A slide's builds as kept: each a box the slide has (or a field), once, in order, with an effect. */
export function cleanBuilds(raw: unknown, boxIds: Iterable<string>): Build[] {
  if (!Array.isArray(raw)) return [];
  const have = new Set(boxIds);
  const seen = new Set<string>();
  const out: Build[] = [];
  for (const r of raw) {
    const id = typeof r === "string" ? r : r && typeof r === "object" ? (r as { id?: unknown }).id : null;
    if (typeof id !== "string" || seen.has(id) || !(have.has(id) || FIELD_KEYS.has(id))) continue;
    const e = r && typeof r === "object" ? (r as { effect?: unknown }).effect : null;
    seen.add(id);
    out.push({ id, effect: (EFFECTS as readonly unknown[]).includes(e) ? (e as BuildEffect) : "fade" });
    if (out.length >= MAX_BUILDS) break;
  }
  return out;
}

/** The transition a slide comes in with: its own, else the deck's, else none. */
export function transitionOf(slide: { transition?: Transition | null } | null | undefined, deck: { transition?: Transition | null } | null | undefined): Transition {
  return slide?.transition ?? deck?.transition ?? "none";
}

/** Where a talk is: the slide on screen and how many of its builds show. */
export interface Step {
  at: number;
  shown: number;
}

/**
 * A click on (dir 1) or back (dir -1), with each slide's count of builds: the next build shows before the next slide,
 * and going back to a slide shows it as it ended. Stays put at either end.
 */
export function stepMove(now: Step, dir: 1 | -1, counts: number[]): Step {
  const n = counts.length;
  if (!n) return { at: 0, shown: 0 };
  const at = Math.max(0, Math.min(n - 1, now.at));
  const shown = Math.max(0, Math.min(counts[at] ?? 0, now.shown));
  if (dir > 0) {
    if (shown < (counts[at] ?? 0)) return { at, shown: shown + 1 };
    return at < n - 1 ? { at: at + 1, shown: 0 } : { at, shown };
  }
  if (shown > 0) return { at, shown: shown - 1 };
  return at > 0 ? { at: at - 1, shown: counts[at - 1] ?? 0 } : { at, shown };
}

/** What each thing on a slide does now: hidden (its click is to come), coming in (its click was the last), or shown. */
export function buildState(builds: Build[] | undefined, shown: number | undefined, fresh: boolean): Map<string, "hidden" | BuildEffect> {
  const out = new Map<string, "hidden" | BuildEffect>();
  if (shown === undefined || !builds?.length) return out;
  builds.forEach((b, i) => {
    if (i >= shown) out.set(b.id, "hidden");
    else if (fresh && i === shown - 1) out.set(b.id, b.effect);
  });
  return out;
}
